import { FlashList } from "@shopify/flash-list";
import {
  AHText,
  Box,
  ListItem,
  SectionHeader,
  AHSeparator,
  Card,
  Input,
} from "arkh";
import React, { useCallback, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorScreenProps,
} from "../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { StorecastSettingsQuery } from "./__generated__/StorecastSettingsQuery.graphql";
import { FormattedMessage, useIntl } from "react-intl";

import { LiveDot } from "../../../ScreenAdmin/StorecastAdminDeviceList/LiveDot";
import { useListContentStyle } from "../../../../components/mobile/useListContentStyle";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { useModalInsets } from "../../../../components/mobile/useModalInsets";
import { useDeleteStorecast } from "./useDeleteStorecast";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { useAlert } from "../../../../common/Alert";
import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";
import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

type ParamList = {
  StorecastSettingsMain: UserLoggedInNavigatorParamList["StorecastSettings"];
};
const Stack = createNativeStackNavigator<ParamList>();

export const StorecastSettings = withWebModal(InnerStorecastSettings, true);

function InnerStorecastSettings(
  props: UserLoggedInNavigatorScreenProps<"StorecastSettings">
) {
  const getHeaderOptions = useNavigationHeaderOptions();
  return (
    <Stack.Navigator>
      <Stack.Screen
        name="StorecastSettingsMain"
        component={StorecastSettingsMain}
        initialParams={props.route.params}
        options={(p) =>
          getHeaderOptions({
            type: "SAVE-INIT",
            onCancel() {
              p.navigation.goBack();
            },
          })
        }
      />
    </Stack.Navigator>
  );
}

type StorecastSettingsMainProps = {
  route: RouteProp<ParamList, "StorecastSettingsMain">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"StorecastSettings">,
    NativeStackNavigationProp<ParamList, "StorecastSettingsMain">
  >;
};

function StorecastSettingsMain(props: StorecastSettingsMainProps) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerStorecatSettingsMain {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerStorecatSettingsMain(
  props: StorecastSettingsMainProps & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<StorecastSettingsQuery>(
    graphql`
      query StorecastSettingsQuery($id: ID!) {
        storecast {
          screens {
            id
            name
            activeSession {
              id
            }
          }
        }
        dashboard(id: $id) {
          id
          name
          screens {
            id
          }
        }
      }
    `,
    {
      id: props.route.params.storecastId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const intl = useIntl();
  const [localName, setLocalName] = useState<null | string>(null);
  const [localScreenIds, setLocalScreenIds] = useState<string[] | null>(null);
  const screenIds = useMemo(
    () =>
      localScreenIds === null
        ? data.dashboard?.screens.map((d) => d.id)
        : (localScreenIds ?? []),
    [data.dashboard?.screens, localScreenIds]
  );
  const [saveMutation, loading] = useMutation(graphql`
    mutation StorecastSettingsMutation(
      $storecastId: ID!
      $setName: String
      $setScreenIds: [String!]
    ) {
      dashboard(dashboardId: $storecastId) {
        setName(title: $setName) {
          id
          name
        }
      }
      dashboard(dashboardId: $storecastId) {
        setScreens(screenIds: $setScreenIds) {
          id
          screens {
            id
          }
        }
      }
    }
  `);

  const Alert = useAlert();

  const isDirty = localName !== null || localScreenIds !== null;
  const save = useCallback(() => {
    saveMutation({
      variables: {
        storecastId: props.route.params.storecastId,
        setName: localName,
        setScreenIds: localScreenIds,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
      onError: (err) => {
        Alert.error("Error", err.message);
      },
    });
  }, [
    Alert,
    localName,
    localScreenIds,
    props.navigation,
    props.route.params.storecastId,
    saveMutation,
  ]);
  const storeScreens = useMemo(
    () =>
      data?.storecast.screens
        .slice()
        .sort((a, b) => {
          const c = (b.activeSession ? 1 : 0) - (a.activeSession ? 1 : 0);
          if (c !== 0) {
            return c;
          }
          return a.name > b.name ? 1 : -1;
        })
        .map((s) => ({ ...s, selected: screenIds?.includes(s.id) })) ?? [],
    [data?.storecast.screens, screenIds]
  );

  const dashboardNameLabel = intl.formatMessage({
    defaultMessage: "Dashboard Name",
    description: "Storecast title input placeholder",
  });
  const ListHeaderComponent = useMemo(() => {
    return (
      <>
        <Box spaceBottom="large">
          <SectionHeader>
            <SectionHeader.Title>{dashboardNameLabel}</SectionHeader.Title>
          </SectionHeader>
          <Card noSpace>
            <Input
              autoCapitalize="words"
              value={localName ?? data.dashboard?.name ?? ""}
              onChangeText={setLocalName}
            />
          </Card>
        </Box>
        {storeScreens.length > 0 ? (
          <SectionHeader>
            <SectionHeader.Title>
              <FormattedMessage
                defaultMessage="Screens Showing this Dashboard"
                description="StorecastSettings screens showing this storecast title"
              />
            </SectionHeader.Title>
          </SectionHeader>
        ) : null}
      </>
    );
  }, [
    dashboardNameLabel,
    data.dashboard?.name,
    localName,
    storeScreens.length,
  ]);

  const [onDeleteStorecast, loadingDelete] = useDeleteStorecast({
    storecastId: props.route.params.storecastId,
    storecastTitle: data.dashboard?.name ?? "",
    onCompleted: () => {
      props.navigation.goBack();
    },
  });
  const intl_tokens = useIntlTokens();
  useNavigationHeader({
    type: "SAVE",
    onCancel() {
      props.navigation.goBack();
    },
    title: intl.formatMessage({
      defaultMessage: "Dashboard Settings",
      description: "Storecast Settings title",
    }),
    saveLoading: loading || loadingDelete,
    onSave: save,
    disabled: !isDirty,
    defaultContentStyle: true,
    more: [
      {
        label: intl_tokens.Delete,
        onPress: () => {
          onDeleteStorecast();
        },
        type: "button",
        color: "negative",
        icon: "trash",
      },
    ],
  });

  const renderItem = useCallback(
    ({
      item,
      index,
    }: {
      item: (typeof storeScreens)[number];
      index: number;
    }) => {
      return (
        <ListItem
          isFirst={index === 0}
          isLast={index === storeScreens.length - 1}
          border="border-primary"
          right={<ListItem.MultiSelectIndicator selected={!!item.selected} />}
          onPress={() => {
            if (item.selected) {
              setLocalScreenIds(
                screenIds?.filter((id) => id !== item.id) ?? []
              );
            } else {
              setLocalScreenIds([...(screenIds ?? []), item.id]);
            }
          }}
        >
          <ListItem.Title>{item.name}</ListItem.Title>
          <Box
            style={{ flexDirection: "row", alignItems: "center" }}
            spaceTop="xxsmall"
          >
            <LiveDot color={item.activeSession ? "positive" : "negative"} />
            <AHText type="detail" spaceLeft="xsmall">
              {item.activeSession ? "Online" : "Offline"}
            </AHText>
          </Box>
        </ListItem>
      );
    },
    [screenIds, storeScreens.length]
  );

  const contentContainerStyle = useListContentStyle();
  const insets = useModalInsets();

  return (
    <FlashList
      extraData={localScreenIds}
      contentInset={{ bottom: insets.bottom }}
      contentContainerStyle={contentContainerStyle}
      data={storeScreens}
      ListHeaderComponent={ListHeaderComponent}
      renderScrollComponent={KeyboardAwareFlashListScrollComponent}
      ItemSeparatorComponent={AHSeparator}
      renderItem={renderItem}
    />
  );
}
